import { useLazyQuery, useMutation } from '@apollo/client';
import Loader from 'components/Loader';
import SlugInput from 'components/SlugInput';
import SmkAsyncSelectGraphql from 'components/SmkAsyncSelect/customSelectGraphql';
import { useFormik } from 'formik';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import Dropzone from 'react-dropzone';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Button,
  Card,
  CardBody,
  CardTitle,
  Col,
  Container,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Row,
} from 'reactstrap';
import { api } from 'utils/axios';
import { API_BASE_URL, LANGUAGE_ARRAY_WITH_STRING, MODULE_TYPE, USER_ROLE } from 'utils/constants';
import { buildURL, handleSaveAttachement, showImage, titleCaseIfExists } from 'utils/helper';
import * as Yup from 'yup';
import Breadcrumbs from '../../../components/Common/Breadcrumb';
import { SMKLinks } from '../../../utils/links';
import { ADD_COMMUNITY_POST, UPDATE_COMMUNITY_POST } from '../graphql/community.mutation';
import {
  COMMUNITY_GROUPS_GET_BY_ID,
  COMMUNITY_GROUPS_SEARCH,
  COMMUNITY_POST_GET_BY_ID,
} from '../graphql/community.query';
import CreatePolled from './components/createdPolled';
import SmkAsyncSelect from 'components/SmkAsyncSelect';

const AddUpdatePosts = () => {
  const history = useHistory();
  const { id: postId, groupId } = useParams();
  const MEDIA_UPLOAD_LIMIT = 5;

  toast.configure();

  const [isUploading, setIsUploading] = useState(false);

  const [isPolled, setIsPolled] = useState(false);
  const [pollErrors, setPollErrors] = useState(null);

  const defaultPollState = {
    title: '',
    options: [''],
    allowMultiple: false,
    endDate: moment().format('YYYY-MM-DDTHH:mm'),
  };

  const [pollData, setPollData] = useState(defaultPollState);

  const [postDetails, setPostDetails] = useState({});
  const [groupDetails, setGroupDetails] = useState({});

  const [getPostDetails, { loading: getPostDetailsLoading }] = useLazyQuery(COMMUNITY_POST_GET_BY_ID, {
    onCompleted: (data) => {
      if (!isEmpty(data?.admin_community_post_getById)) {
        setPostDetails(data?.admin_community_post_getById);
      }
    },
    onError: () => {
      toast.error('Something went wrong!');
    },
  });

  const [getGroupDetails, { loading: getGroupDetailsLoading }] = useLazyQuery(COMMUNITY_GROUPS_GET_BY_ID, {
    onCompleted: (data) => {
      if (!isEmpty(data?.admin_community_group_getById)) {
        setGroupDetails(data?.admin_community_group_getById);
      }
    },
    onError: () => {
      toast.error('Something went wrong!');
    },
  });

  const [addPostRequest, { loading: addPostLoading }] = useMutation(ADD_COMMUNITY_POST, {
    onCompleted: (data) => {
      if (!isEmpty(data?.admin_community_post_add)) {
        toast.success('Success! Post has been added.');

        let url = '';

        if (groupId) {
          url = buildURL(SMKLinks.COMMUNITY_GROUPS_DETAILS, {
            id: groupId,
          });
        } else {
          url = buildURL(SMKLinks.COMMUNITY_POSTS_LIST);
        }
        history.replace(url);
      }
    },
    onError: () => {
      toast.error('Something went wrong!');
    },
  });

  const [updatePostRequest, { loading: updatePostLoading }] = useMutation(UPDATE_COMMUNITY_POST, {
    onCompleted: (data) => {
      if (!isEmpty(data?.admin_community_post_update)) {
        toast.success(`Success! Post has been updated.`);
        window.location.reload();
      }
    },
    onError: () => {
      toast.error('Something went wrong!');
    },
  });

  const editInitialValues = () => {
    return {
      active: postDetails?.active,
      deleted: postDetails?.deleted,
      title: postDetails?.title,
      slug: postDetails?.slug,
      content: postDetails?.content,
      group: {
        label: postDetails?.group?.name,
        value: postDetails?.group?.id,
      },
      isPinned: postDetails?.isPinned,
      isAnonymous: postDetails?.isAnonymous,
      language: postDetails?.language,
      media: postDetails?.media,
      createdBy: !isEmpty(postDetails?.createdBy)
        ? { label: postDetails?.createdBy?.displayName, value: postDetails?.createdById }
        : null,
    };
  };

  const getInitialValues = () => ({
    active: true,
    deleted: false,
    isPinned: false,
    isAnonymous: false,
    title: '',
    slug: '',
    content: '',
    language: LANGUAGE_ARRAY_WITH_STRING[0]?.value,
    media: null,
    createdBy: null,
    group: !isEmpty(groupDetails) ? { label: groupDetails?.name, value: groupDetails?.id } : null,
  });

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: false,
    initialValues: isEmpty(postDetails) ? { ...getInitialValues() } : { ...editInitialValues() },
    onSubmit: (values) => {
      const payload = {
        ...values,
        group: undefined,
        groupId: values?.group?.value,
        createdById: values?.createdBy?.value,
        createdBy: undefined,
      };

      if (!isEmpty(values?.media)) {
        payload.media = values?.media?.map((e) => ({
          id: String(e.id),
        }));
      } else {
        payload.media = null;
      }

      if (isPolled) {
        let polledErrorsObj = {
          title: isEmpty(pollData?.title) ? 'Poll Title is required' : '',
          customTitle: pollData?.title.length > 150 ? 'Poll Title is not greater than 150 words.' : '',
          options: isEmpty(pollData?.options?.filter((e) => e !== '')) ? 'Options is required' : '',
          endDate: isEmpty(pollData?.endDate) ? 'End Date is required' : '',
        };

        const isPolledValid = Object.values(polledErrorsObj)?.every((e) => e === '');

        if (!isPolledValid) {
          setPollErrors(polledErrorsObj);
          return;
        } else {
          payload.poll = {
            ...pollData,
          };
        }
      } else {
        payload.poll = null;
        payload.isPolled = undefined;
      }

      isEmpty(postId)
        ? addPostRequest({
            variables: {
              createDTO: payload,
            },
          })
        : updatePostRequest({
            variables: {
              postId: postId,
              createDTO: payload,
            },
          });
    },
    validationSchema: Yup.object().shape({
      title: Yup.string()
        .required('Please provide title')
        .max(150, 'Title must not exceed 150 characters'),

      slug: Yup.string()
        .required('Please provide slug')
        .max(150, 'Slug must not exceed 150 characters'),

      content: Yup.string()
        .required('Please provide content')
        .max(1000, 'Content must not exceed 1000 characters'),

      group: Yup.object()
        .required('Please provide group')
        .nullable(true),

      createdBy: Yup.object()
        .required('Please provide createdBy')
        .nullable(true),
    }),
  });

  useEffect(() => {
    if (groupId) {
      getGroupDetails({
        variables: {
          groupId,
        },
      });
    }

    if (postId) {
      getPostDetails({
        variables: {
          postId,
        },
      });
    }
  }, [groupId, postId]);

  useEffect(() => {
    if (!isEmpty(postDetails?.poll)) {
      setIsPolled(true);
      setPollData({
        ...postDetails?.poll,
        active: undefined,
        deleted: undefined,
        id: undefined,
        imageId: undefined,
        __typename: undefined,
      });
    }
  }, [postDetails]);

  const handleSelectChange = (res) => {
    if (!isEmpty(res)) {
      formik.setFieldValue('group', {
        label: res?.name,
        value: res?.id,
      });
    } else {
      formik.setFieldValue('group', null);
    }
  };

  const uploadFile = async (file, forType) => {
    if (file) {
      setIsUploading(true);
      const payload = {
        isAsset: true,
        renameOriginal: true,
        renameOriginalRaw: true,
      };

      const formData = new FormData();
      formData.append('file', file);

      try {
        const fileResponse = await api({
          method: 'POST',
          url: `${API_BASE_URL}/attachment/upload?config=${btoa(JSON.stringify(payload))}`,
          data: formData,
        });

        const assetSavedFile = await handleSaveAttachement(fileResponse, forType);
        return assetSavedFile;
      } catch (err) {
        console.warn('Error uploading file:', err);
        toast.error(err.message || 'Oops! Something went wrong');
      } finally {
        setIsUploading(false);
      }
    }
  };

  // Upload Media
  const handleFileSelect = async (files) => {
    const MAX_FILE_SIZE_MB = 10; // Maximum file size in MB
    const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024; // Convert to bytes

    // File count Check
    if (
      files.length > MEDIA_UPLOAD_LIMIT ||
      (!isEmpty(formik.values.media) && formik.values.media.length >= MEDIA_UPLOAD_LIMIT)
    ) {
      toast.error(`Maximum ${MEDIA_UPLOAD_LIMIT} files can be uploaded at once.`);
      return;
    }

    // File Size Check
    const oversizedFiles = files.filter((file) => file.size > MAX_FILE_SIZE_BYTES);
    if (oversizedFiles.length > 0) {
      toast.error(`Files must be smaller than ${MAX_FILE_SIZE_MB} MB.`);
      return;
    }

    // Invalid File Check
    const invalidFiles = files.some((file) => {
      const isImage = file.type?.startsWith('image');
      const isVideo = file.type?.startsWith('video');
      return !(isImage || isVideo);
    });

    if (invalidFiles > 0) {
      toast.error('Invalid file type. Please select a valid image or video file.');
      return;
    }

    if (files.length > 0) {
      setIsUploading(true);
      try {
        const uploadedFilesPromises = await files.map((file) => uploadFile(file, MODULE_TYPE.FORUM));
        const uploadedFiles = await Promise.all(uploadedFilesPromises);
        if (uploadedFiles.length > 0) {
          formik.setFieldValue('media', [...(formik.values.media || []), ...uploadedFiles]);
        }
      } catch (error) {
        console.error('File Upload Error:', error);
      } finally {
        setIsUploading(false);
      }
    }
  };

  return (
    <div className="page-content">
      <Loader
        isActive={addPostLoading || updatePostLoading || isUploading || getGroupDetailsLoading || getPostDetailsLoading}
      />
      <Container fluid>
        <Breadcrumbs title="Community Post" breadcrumbItem={`${isEmpty(postId) ? 'Add' : 'Edit'}  Community Post`} />

        <Form onSubmit={formik.handleSubmit}>
          <Row>
            <Col md={9}>
              <Card>
                <CardBody>
                  <Row className="align-items-center">
                    <Col md={12}>
                      <FormGroup className="mb-4">
                        {!isEmpty(groupDetails) ? (
                          <h5>Group name: {groupDetails?.name}</h5>
                        ) : (
                          <>
                            <Label for="group" className="form-label  ">
                              Group <span className="text-danger">*</span>
                            </Label>
                            <SmkAsyncSelectGraphql
                              disable
                              multi={false}
                              acceptedKey={'name'}
                              acceptedValue={'id'}
                              placeholder="Type Group name.."
                              searchKeyName="name"
                              query={COMMUNITY_GROUPS_SEARCH}
                              queryName={'admin_community_group_search'}
                              dtoName={'searchDTO'}
                              value={formik.values?.group}
                              filters={{
                                autoComplete: true,
                                active: true,
                                page: 1,
                                size: 500,
                                sortBy: 'updatedDate',
                                sortOrder: -1,
                                language: formik.values.language,
                              }}
                              onChange={handleSelectChange}
                            />
                            <FormFeedback className="d-block">{formik.errors.group}</FormFeedback>
                          </>
                        )}
                      </FormGroup>
                    </Col>
                    <Col md={12}>
                      <FormGroup className="mb-4">
                        <Label for="title" className="form-label  ">
                          Title <span className="text-danger">*</span>
                        </Label>
                        <Input
                          id="title"
                          name="title"
                          type="text"
                          className="form-control"
                          placeholder="Title.."
                          invalid={!!(formik.touched.title && formik.errors.title)}
                          {...formik.getFieldProps('title')}
                        />
                        <FormFeedback>{formik.errors.title}</FormFeedback>
                      </FormGroup>
                    </Col>

                    <Col md={12} className="mb-4">
                      <SlugInput
                        required={true}
                        formik={formik}
                        ID={postId}
                        nameValue={formik.values.title}
                        formGroupClassName={'mt-0'}
                        showResetRegenerate={true}
                        currentSlug={postDetails?.slug}
                      />
                    </Col>

                    <Col md={12}>
                      <FormGroup className="mb-4">
                        <Label for="content" className="form-label  ">
                          Content <span className="text-danger">*</span>
                        </Label>
                        <Input
                          id="content"
                          name="content"
                          style={{ height: '200px' }}
                          type="textarea"
                          className="form-control"
                          placeholder="Content.."
                          invalid={!!(formik.touched.content && formik.errors.content)}
                          {...formik.getFieldProps('content')}
                        />
                        <FormFeedback>{formik.errors.content}</FormFeedback>
                      </FormGroup>
                    </Col>
                  </Row>
                </CardBody>
              </Card>

              <Card>
                <CardBody>
                  <div>
                    <Label className="col-form-label">Upload Media</Label>
                    <FormGroup>
                      <Dropzone
                        acceptedFiles={{
                          'image/*': [],
                          'video/*': [],
                        }}
                        onDrop={(acceptedFile) => {
                          handleFileSelect(acceptedFile);
                        }}>
                        {({ getRootProps, getInputProps }) => (
                          <div className="dropzone">
                            <div className="dz-message needsclick p-0" {...getRootProps()}>
                              <input {...getInputProps()} />
                              <div className="dz-message needsclick">
                                <div className="mb-2">
                                  <i className="display-2 text-muted bx bxs-cloud-upload" />
                                </div>
                                <h4>Drop files here or click to upload.</h4>
                              </div>
                            </div>
                          </div>
                        )}
                      </Dropzone>

                      {!isEmpty(formik.values.media) && (
                        <div className="mt-4">
                          <h5 className="mb-3">Uploaded Files :-</h5>
                          <div className="row mx-0">
                            {formik.values.media?.map((file, index) => {
                              return (
                                <div key={index} className={`col-md-6`}>
                                  <div className="d-flex flex-column border p-3 rounded-2 mb-4">
                                    {file?.type?.includes('image') ? (
                                      <img
                                        src={showImage(file?.original)}
                                        className="mb-4"
                                        style={{ height: '150px', objectFit: 'contain' }}
                                      />
                                    ) : file?.type?.includes('video') ? (
                                      <video
                                        controls
                                        style={{ maxHeight: '150px' }}
                                        className="w-full mb-4"
                                        src={showImage(file?.original)}
                                      />
                                    ) : (
                                      <div className="w-full h-32 flex items-center justify-center">
                                        No Data Available
                                      </div>
                                    )}
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const updatedFiles = formik.values.media?.filter((_, i) => i !== index);
                                        formik.setFieldValue('media', updatedFiles);
                                      }}
                                      className="btn btn-outline-danger mt-3">
                                      Delete
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </FormGroup>
                  </div>
                </CardBody>
              </Card>

              {isPolled && (
                <Card>
                  <CardBody>
                    <CreatePolled
                      setPollData={setPollData}
                      pollData={pollData}
                      pollErrors={pollErrors}
                      setPollErrors={setPollErrors}
                    />
                  </CardBody>
                </Card>
              )}
            </Col>
            <Col md={3}>
              <Card>
                <CardBody>
                  <Button type="submit" color="primary" className="mb-3 w-100">
                    {postId ? 'Save' : 'Add'}
                  </Button>

                  <Button
                    className="w-100"
                    color="light"
                    onClick={() => {
                      let url = '';

                      if (groupId) {
                        url = buildURL(SMKLinks.COMMUNITY_GROUPS_DETAILS, {
                          id: groupId,
                        });
                      } else {
                        url = buildURL(SMKLinks.COMMUNITY_POSTS_LIST);
                      }
                      history.replace(url);
                    }}>
                    Cancel
                  </Button>
                </CardBody>
              </Card>
              <Card>
                <CardBody>
                  {['active', 'deleted', 'isPinned', 'isAnonymous']?.map((elem, index) => (
                    <FormGroup className={`mb-3`} key={index}>
                      <Label className="mb-3">
                        {elem === 'isPinned'
                          ? 'isPinned'
                          : elem === 'isAnonymous'
                          ? 'isAnonymous Comment Allowed'
                          : titleCaseIfExists(elem)}
                      </Label>
                      <div className="d-flex align-items-center">
                        {[true, false].map((option, i) => (
                          <div key={`${elem}_${i}`} className="form-check d-inline-block me-4">
                            <input
                              type="radio"
                              id={`${elem}_${option}`}
                              name={elem}
                              className="form-check-input"
                              checked={formik.values[elem] === option}
                              onChange={() => formik.setFieldValue(`${elem}`, option)}
                            />
                            <label className="form-check-label" htmlFor={`${elem}_${option}`}>
                              {option ? 'Yes' : 'No'}
                            </label>
                          </div>
                        ))}
                      </div>
                    </FormGroup>
                  ))}

                  <FormGroup className={`mb-3`}>
                    <Label className="mb-3">is Polled </Label>
                    <div className="d-flex align-items-center">
                      {[true, false].map((option, i) => (
                        <div key={`isPolled_${i}`} className="form-check mb-3 d-inline-block me-4">
                          <input
                            type="radio"
                            id={`isPolled_${option}`}
                            name="isPolled"
                            className="form-check-input"
                            checked={isPolled === option}
                            onChange={() => {
                              setIsPolled(option);
                            }}
                          />
                          <label className="form-check-label" htmlFor={`isPolled_${option}`}>
                            {option ? 'Yes' : 'No'}
                          </label>
                        </div>
                      ))}
                    </div>
                  </FormGroup>
                </CardBody>
              </Card>

              <Card>
                <CardBody>
                  <FormGroup className="mb-3">
                    <Label className="form-label ">
                      CreatedBy
                      <span className="text-danger">*</span>
                    </Label>
                    <SmkAsyncSelect
                      acceptedKey={'displayName'}
                      // combineLabelKey={'email'}
                      searchKeyName="name"
                      acceptedValue={'id'}
                      placeholder="Type user name.."
                      onChange={(res) => {
                        formik.setFieldValue(
                          'createdBy',
                          isEmpty(res)
                            ? null
                            : {
                                ...res,
                                label: res?.displayName,
                                value: res?.id,
                              }
                        );
                      }}
                      fetchUrl={`${API_BASE_URL}/user/admin/search`}
                      filters={{
                        autoComplete: true,
                        page: 1,
                        size: 10,
                        sortBy: 'id',
                        sortOrder: 0,
                        roleIds: [USER_ROLE.STAFF, USER_ROLE.OFFLINE_USERS],
                      }}
                      value={formik.values.createdBy || null}
                    />
                    {formik.touched.createdBy && (
                      <div className="invalid-feedback d-block">{formik.errors.createdBy}</div>
                    )}
                  </FormGroup>
                </CardBody>
              </Card>

              {/* <Card className="border">
                <CardBody>
                  <Row>
                    <Col md={12}>
                      <CardTitle className="mb-3">Language </CardTitle>
                      {LANGUAGE_ARRAY_WITH_STRING?.map((option, i) => (
                        <div key={`language_${i}`} className="form-check mb-3 d-inline-block me-4">
                          <input
                            type="radio"
                            id={`language_${option?.value}`}
                            name="language"
                            className="form-check-input"
                            checked={formik.values.language === option?.value}
                            onChange={() => formik.setFieldValue('language', option?.value)}
                          />
                          <label className="form-check-label" htmlFor={`language_${option?.value}`}>
                            {option?.label}
                          </label>
                        </div>
                      ))}
                    </Col>
                  </Row>
                </CardBody>
              </Card> */}
            </Col>
          </Row>
        </Form>
      </Container>
    </div>
  );
};

export default AddUpdatePosts;
